import { useEffect, useRef, useState } from 'react'
import { FileText, Trash2, Upload, Loader2, ExternalLink } from 'lucide-react'
import { Button } from '@/components/ui/Button'
import { ConfirmDialog } from '@/components/ui/ConfirmDialog'
import { CompaniesService, type CompanyDocument } from '@/services/modules/CompaniesService'
import { toast } from '@/components/ui/Toast'
import { extractApiError } from '@/lib/api-errors'
import { useAuthority } from '@/hooks/useAuthority'

function humanSize(bytes: number | null): string {
  if (!bytes) return '—'
  if (bytes < 1024) return `${bytes} B`
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(0)} KB`
  return `${(bytes / 1024 / 1024).toFixed(1)} MB`
}

/** Seção "Documentos" do form de empresa — upload de arquivos diversos. */
export function CompanyDocumentsSection({ companyId }: { companyId: string | null }) {
  // Enviar e remover documentos usam a permissão de edição da empresa.
  const { can } = useAuthority()
  const canManage = can('commercial.companies.edit')
  const [docs, setDocs] = useState<CompanyDocument[]>([])
  const [loading, setLoading] = useState(false)
  const [uploading, setUploading] = useState(false)
  const [toDelete, setToDelete] = useState<CompanyDocument | null>(null)
  const [busy, setBusy] = useState(false)
  const inputRef = useRef<HTMLInputElement | null>(null)

  useEffect(() => {
    if (!companyId) return
    setLoading(true)
    CompaniesService.documents(companyId).then(setDocs).catch(() => setDocs([])).finally(() => setLoading(false))
  }, [companyId])

  if (!companyId) {
    return <p className="text-xs text-gray-400">Salve a empresa primeiro para anexar documentos.</p>
  }

  async function onPick(e: React.ChangeEvent<HTMLInputElement>) {
    const file = e.target.files?.[0]
    if (!file || !companyId) return
    if (file.size > 20 * 1024 * 1024) {
      toast.error({ title: 'Arquivo muito grande', description: 'Limite de 20 MB por documento.' })
      e.target.value = ''
      return
    }
    setUploading(true)
    try {
      const doc = await CompaniesService.uploadDocument(companyId, file)
      setDocs((prev) => [doc, ...prev])
      toast.success({ title: 'Documento enviado', description: doc.name })
    } catch (err) {
      toast.error({ title: 'Falha no upload', description: extractApiError(err).message })
    } finally {
      setUploading(false)
      if (inputRef.current) inputRef.current.value = ''
    }
  }

  async function confirmDelete() {
    if (!toDelete || !companyId) return
    setBusy(true)
    try {
      await CompaniesService.deleteDocument(companyId, toDelete.id)
      setDocs((prev) => prev.filter((d) => d.id !== toDelete.id))
      toast.success({ title: 'Documento removido', description: toDelete.name })
      setToDelete(null)
    } catch (err) {
      toast.error({ title: 'Não foi possível remover', description: extractApiError(err).message })
    } finally { setBusy(false) }
  }

  return (
    <div className="space-y-4">
      {canManage && <div>
        <input ref={inputRef} type="file" className="hidden" onChange={onPick} />
        <Button type="button" variant="secondary" iconLeft={uploading ? <Loader2 size={14} className="animate-spin" /> : <Upload size={14} />}
          disabled={uploading} onClick={() => inputRef.current?.click()}>
          {uploading ? 'Enviando…' : 'Enviar documento'}
        </Button>
        <p className="mt-1 text-[11px] text-gray-400">Qualquer tipo de arquivo, até 20 MB.</p>
      </div>}

      {loading ? (
        <div className="flex items-center justify-center py-8"><Loader2 size={20} className="animate-spin text-gray-400" /></div>
      ) : docs.length === 0 ? (
        <div className="rounded-md border border-dashed border-gray-300 p-6 text-center text-sm text-gray-400">Nenhum documento anexado.</div>
      ) : (
        <ul className="divide-y divide-gray-100 rounded-md ring-1 ring-gray-200">
          {docs.map((d) => (
            <li key={d.id} className="flex items-center justify-between gap-3 px-3 py-2.5">
              <span className="flex min-w-0 items-center gap-2 text-sm text-gray-700">
                <FileText size={15} className="shrink-0 text-gray-400" />
                <span className="break-words">{d.name}</span>
                <span className="shrink-0 text-[11px] text-gray-400">· {humanSize(d.size)}{d.createdAt ? ` · ${d.createdAt}` : ''}</span>
              </span>
              <span className="flex shrink-0 items-center gap-1">
                {d.url && (
                  <a href={d.url} target="_blank" rel="noreferrer"
                    className="cursor-pointer inline-flex items-center gap-1 rounded-md px-2 py-1 text-xs text-brand-600 hover:bg-brand-50">
                    <ExternalLink size={12} /> Abrir
                  </a>
                )}
                {canManage && (
                  <button type="button" onClick={() => setToDelete(d)} aria-label="Remover"
                    className="cursor-pointer inline-flex items-center justify-center w-7 h-7 rounded-md text-rose-600 hover:bg-rose-50">
                    <Trash2 size={14} />
                  </button>
                )}
              </span>
            </li>
          ))}
        </ul>
      )}

      <ConfirmDialog
        open={!!toDelete}
        onClose={() => setToDelete(null)}
        onConfirm={confirmDelete}
        loading={busy}
        variant="danger"
        title="Remover documento?"
        description={`Remover "${toDelete?.name ?? ''}"? Esta ação não pode ser desfeita.`}
        confirmLabel="Remover"
      />
    </div>
  )
}
